import type { OrderChatMessage, OrderChatThread } from '../types';

/* ============================================================
   33f30c97: pure view model for the order chat panel — bubble
   sides, unread/lock cues, send gating. No API calls here.
   ============================================================ */

export type OrderChatRole = 'customer' | 'provider';

export interface OrderChatBubble {
  id: string;
  body: string;
  /** fa58b0eb: attachment URLs (≤3) rendered as thumbnails above the text. */
  photoUrls: string[];
  sentAtIso: string;
  /** 'mine' = authored by the viewing party. */
  side: 'mine' | 'theirs';
  /** Short author label for counterpart bubbles. */
  senderLabel: string;
}

export interface OrderChatView {
  bubbles: OrderChatBubble[];
  unreadLabel: string | null;
  lockReason: string | null;
  emptyCopy: string | null;
  canSend: boolean;
  showLoadMore: boolean;
}

const MAX_BODY_LENGTH = 2000;

/**
 * 10fb5d39: one unread-chat wording everywhere — the thread panel's badge
 * and the list-row/queue-card badges read the same copy so the surfaces
 * never disagree.
 */
export function chatUnreadLabel(unread: number): string {
  return unread === 1 ? '1 новое сообщение' : `${unread} новых`;
}

export function buildOrderChatView(input: {
  thread: OrderChatThread | null;
  role: OrderChatRole;
  draft: string;
  /** fa58b0eb: staged attachments — photo-only drafts are sendable. */
  photoCount?: number;
}): OrderChatView {
  const { thread, role, draft } = input;
  const photoCount = input.photoCount ?? 0;
  const bubbles = (thread?.messages ?? []).map((m: OrderChatMessage) => toBubble(m, role));

  const unread = thread?.unreadCount ?? 0;
  const canPost = thread?.canPost ?? false;
  const draftFits = draft.trim().length > 0 && draft.length <= MAX_BODY_LENGTH;
  const hasPayload = draftFits || photoCount > 0;

  return {
    bubbles,
    unreadLabel: unread > 0 ? chatUnreadLabel(unread) : null,
    lockReason: thread?.lockReason ?? null,
    // Thread exists and is readable but has no messages yet.
    emptyCopy: thread !== null && bubbles.length === 0
      ? 'Обсудите детали заказа — цвет, сроки и доставку.'
      : null,
    canSend: canPost && hasPayload,
    showLoadMore: thread?.hasMore ?? false,
  };
}

function toBubble(m: OrderChatMessage, role: OrderChatRole): OrderChatBubble {
  // The customer chats with the provider and vice versa.
  const mine = role === 'customer' ? !m.isFromProvider : m.isFromProvider;
  return {
    id: m.messageId,
    body: m.body,
    photoUrls: m.photoUrls ?? [],
    sentAtIso: m.sentAtUtc,
    side: mine ? 'mine' : 'theirs',
    senderLabel: m.isFromProvider ? 'Исполнитель' : 'Заказчик',
  };
}

/** «14:32» time chip for a bubble (HH:MM, ru locale, 24h). */
export function chatBubbleTime(sentAtIso: string): string {
  const d = new Date(sentAtIso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

/** Draft helper: whether the counter (N/2000) should warn. */
export function draftOverLimit(draft: string): boolean {
  return draft.length > MAX_BODY_LENGTH;
}
